// components/sections/UsageConditionsSection.js
import React from 'react';
import InteractiveField from '../common/InteractiveField';
import MultilineField from '../common/MultilineField';


/**
 * Компонент для раздела "5. Условия использования Помещения".
 * @param {object} data - Объект состояния для этого раздела.
 * @param {function} onChange - Функция для обновления состояния.
 */
const UsageConditionsSection = ({ data, onChange }) => {
  // Безопасная деструктуризация с фолбэками
  const {
    smoking = 'forbidden', // 'forbidden' | 'allowed'
    openFlames = 'forbidden', // 'forbidden' | 'allowed'
    pets = 'forbidden', // 'forbidden' | 'allowed'
    meterReadings = { responsible: 'tenant_to_landlord', dayOfMonth: 25 },
    includeOtherConditions = false,
    otherConditions = '',
    includeSmoking = false,
    includeOpenFlames = false,
    includePets = false,
    routineRepair = { person: 'tenant', includeWorkList: false, workList: '', includeRedevelopmentBan: true }
  } = data;

  // Универсальный обработчик для простых переключателей
  const handleOptionChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  // Обработчик для вложенного объекта meterReadings
  const handleMeterReadingsChange = (field, value) => {
    onChange({ ...data, meterReadings: { ...meterReadings, [field]: value } });
  };

  // Обработчик для переключения опциональных полей
  const handleToggleChange = (field) => {
    onChange({ ...data, [field]: !data[field] });
  };

  const handleRoutineRepairChange = (field, value) => {
    onChange({ ...data, routineRepair: { ...routineRepair, [field]: value } });
  };

  const handleRoutineRepairToggle = (field) => {
    onChange({ ...data, routineRepair: { ...routineRepair, [field]: !routineRepair[field] } });
  };

  // Счетчик для нумерации пунктов
  // 5.1 — текущий ремонт (всегда), 5.2 — показания приборов учёта (всегда), далее — опциональные
  let clauseCounter = 2;

  // Функция для получения следующего номера пункта
  const getNextClauseNumber = () => {
    clauseCounter++;
    return clauseCounter;
  };

  return (
    <div className="document-preview-editor">
      <h2 className="special-section-title" style={{ width: '100%', display: 'block', textAlign: 'center', marginBottom: '24px' }}>
        5. Условия использования Помещения
      </h2>

      {/* --- 5.1. Текущий ремонт (обязательная секция) --- */}
      <div className="configurable-section dynamic-content-block active-green">
        <p className="document-preamble">
          <b>5.1.</b> {routineRepair.person === 'tenant' ? 'Наниматель' : 'Наймодатель'} осуществляет текущий ремонт Помещения за свой счет.
          {routineRepair.includeWorkList && (
            <> В текущий ремонт входят следующие работы: <MultilineField value={routineRepair.workList} onChange={(v) => handleRoutineRepairChange('workList', v)} placeholder="при необходимости ремонт кранов, замена лампочек, подкрашивание стен и потолков от следов износа" isRequired={true} minRows={1} maxRows={5} /></>
          )}
        </p>
        {routineRepair.includeRedevelopmentBan && (
          <p className="document-preamble">Наниматель не вправе производить переустройство и реконструкцию Помещения без согласия Наймодателя.</p>
        )}
        <div className="option-pills-group" style={{ marginTop: '16px' }}>
          <button
            className={`option-pill ${routineRepair.person === 'tenant' ? 'selected' : ''}`}
            onClick={() => handleRoutineRepairChange('person', 'tenant')}
          >
            Осуществляет Наниматель
          </button>
          <button
            className={`option-pill ${routineRepair.person === 'landlord' ? 'selected' : ''}`}
            onClick={() => handleRoutineRepairChange('person', 'landlord')}
          >
            Осуществляет Наймодатель
          </button>
          <button
            className={`option-pill-toggle ${routineRepair.includeWorkList ? 'selected' : ''}`}
            onClick={() => handleRoutineRepairToggle('includeWorkList')}
          >
            {routineRepair.includeWorkList ? '✓ Описание текущего ремонта' : '+ Описание текущего ремонта'}
          </button>
          <button
            className={`option-pill-toggle ${routineRepair.includeRedevelopmentBan ? 'selected' : ''}`}
            onClick={() => handleRoutineRepairToggle('includeRedevelopmentBan')}
          >
            {routineRepair.includeRedevelopmentBan ? '✓ Запрет на переустройство' : '+ Запрет на переустройство'}
          </button>
        </div>
      </div>

      {/* --- 5.2. Показания приборов учета (обязательная секция) --- */}
      <div className="configurable-section dynamic-content-block active-purple">
        <p className="document-preamble">
          <b>5.2.</b>
          {meterReadings.responsible === 'tenant_to_landlord' && (
            <>
              {' '}Наниматель сообщает показания приборов учета коммунальных услуг Наймодателю{' '}
              <InteractiveField value={meterReadings.dayOfMonth} onChange={(v) => handleMeterReadingsChange('dayOfMonth', v)} type="number" />
              {' '}числа каждого месяца.
            </>
          )}
          {meterReadings.responsible === 'tenant_to_org' && (
            <>
              {' '}Наниматель сообщает показания приборов учета коммунальных услуг организации, осуществляющей учет показаний{' '}
              <InteractiveField value={meterReadings.dayOfMonth} onChange={(v) => handleMeterReadingsChange('dayOfMonth', v)} type="number" />
              {' '}числа каждого месяца.
            </>
          )}
          {meterReadings.responsible === 'landlord_self' && ' Наймодатель самостоятельно ведёт учет показаний приборов учета коммунальных услуг.'}
        </p>
        <div className="option-pills-group" style={{ marginTop: '16px' }}>
          <button className={`option-pill ${meterReadings.responsible === 'tenant_to_landlord' ? 'selected' : ''}`} onClick={() => handleMeterReadingsChange('responsible', 'tenant_to_landlord')}>Наниматель сообщает Наймодателю</button>
          <button className={`option-pill ${meterReadings.responsible === 'tenant_to_org' ? 'selected' : ''}`} onClick={() => handleMeterReadingsChange('responsible', 'tenant_to_org')}>Наниматель сообщает организации</button>
          <button className={`option-pill ${meterReadings.responsible === 'landlord_self' ? 'selected' : ''}`} onClick={() => handleMeterReadingsChange('responsible', 'landlord_self')}>Наймодатель самостоятельно ведёт учет</button>
        </div>
      </div>

      {/* --- Опциональные секции --- */}
      {/* --- Курение --- */}
      <div className="optional-field-block dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button
            className={`option-pill-toggle ${includeSmoking ? 'selected' : ''}`}
            onClick={() => handleToggleChange('includeSmoking')}
          >
            {includeSmoking ? '✓ Курение в Помещении' : '+ Курение в Помещении'}
          </button>
        </div>
        {includeSmoking && (
          <>
            <p className="document-preamble">
              <b>5.{getNextClauseNumber()}.</b> Нанимателю {smoking === 'forbidden' ? 'запрещено' : 'разрешено'} в Помещении курить и использовать любые другие средства, выделяющие никотин, дым, пар и другие продукты горения.
            </p>
            <div className="option-pills-group" style={{ marginTop: '16px' }}>
              <button className={`option-pill ${smoking === 'forbidden' ? 'selected' : ''}`} onClick={() => handleOptionChange('smoking', 'forbidden')}>Запрет</button>
              <button className={`option-pill ${smoking === 'allowed' ? 'selected' : ''}`} onClick={() => handleOptionChange('smoking', 'allowed')}>Разрешение</button>
            </div>
          </>
        )}
      </div>

      {/* --- 5.2. Открытые источники огня --- */}
      <div className="optional-field-block dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button
            className={`option-pill-toggle ${includeOpenFlames ? 'selected' : ''}`}
            onClick={() => handleToggleChange('includeOpenFlames')}
          >
            {includeOpenFlames ? '✓ Использование открытых источников огня' : '+ Использование открытых источников огня'}
          </button>
        </div>
        {includeOpenFlames && (
          <>
            <p className="document-preamble">
              <b>5.{getNextClauseNumber()}.</b> Нанимателю {openFlames === 'forbidden' 
                ? 'запрещено в Помещении использовать открытые источники огня, кроме тех, которые относятся к оборудованию Помещения.' 
                : 'разрешено в Помещении использовать открытые источники огня, при постоянном присутствии во время использования.'
              }
            </p>
            <div className="option-pills-group" style={{ marginTop: '16px' }}>
              <button className={`option-pill ${openFlames === 'forbidden' ? 'selected' : ''}`} onClick={() => handleOptionChange('openFlames', 'forbidden')}>Запрет на использование</button>
              <button className={`option-pill ${openFlames === 'allowed' ? 'selected' : ''}`} onClick={() => handleOptionChange('openFlames', 'allowed')}>Разрешение на использование</button>
            </div>
          </>
        )}
      </div>

      {/* --- 5.3. Домашние животные --- */}
      <div className="optional-field-block dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button
            className={`option-pill-toggle ${includePets ? 'selected' : ''}`}
            onClick={() => handleToggleChange('includePets')}
          >
            {includePets ? '✓ Содержание домашних животных' : '+ Содержание домашних животных'}
          </button>
        </div>
        {includePets && (
          <>
            <p className="document-preamble">
              <b>5.{getNextClauseNumber()}.</b> Нанимателю {pets === 'forbidden' 
                ? 'запрещено содержать или временно приводить животных в Помещение.' 
                : 'разрешено содержать животных в Помещение, при соблюдении Федерального закона от 27.12.2018 N 498-ФЗ.'
              }
            </p>
            <div className="option-pills-group" style={{ marginTop: '16px' }}>
              <button className={`option-pill ${pets === 'forbidden' ? 'selected' : ''}`} onClick={() => handleOptionChange('pets', 'forbidden')}>Запрет</button>
              <button className={`option-pill ${pets === 'allowed' ? 'selected' : ''}`} onClick={() => handleOptionChange('pets', 'allowed')}>Разрешение</button>
            </div>
          </>
        )}
      </div>

      {/* --- Иные условия --- */}
      <div className="configurable-section dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
            <button className={`option-pill-toggle ${includeOtherConditions ? 'selected' : ''}`} onClick={() => handleToggleChange('includeOtherConditions')}>
                {includeOtherConditions ? '✓ Иные условия использования' : '+ Иные условия использования Помещения'}
            </button>
        </div>

        {includeOtherConditions && (
            <p className="document-preamble">
                <b>5.{getNextClauseNumber()}.</b>{' '}
                <MultilineField 
                  value={otherConditions} 
                  onChange={(v) => handleOptionChange('otherConditions', v)} 
                  placeholder="Нанимателю запрещено шуметь после 23:00" 
                  isRequired={true} 
                  minRows={1}
                  maxRows={5}
                />
            </p>
        )}
      </div>
    </div>
  );
};

export default UsageConditionsSection;